"use client";
import { useEffect } from "react";
import { logClientSideError } from "@/utils/logging/clientErrorLogger";

/**
 * Captures the two classes of production error that our React error boundaries
 * can NOT see, and routes them into the existing /api/log-error -> Graylog
 * pipeline (where `resolveSourceMap.ts` symbolicates the stack).
 *
 *  1. Uncaught errors from scripts we do not control — tenant custom scripts,
 *     marketing tags, and native-app WebView bridges. These never reach a React
 *     boundary, so today they are invisible to us and only show up as anonymous
 *     counts in RUM. `event.filename` is the key field: it names the script.
 *
 *  2. React hydration mismatches (minified error #418). React treats these as
 *     *recoverable* — it logs them and re-renders on the client rather than
 *     throwing — so an error boundary never fires. They matter both as bugs and
 *     as performance: React discards the server HTML and rebuilds the tree.
 *
 * Volume control: identical errors are reported once, and the listener stops
 * after MAX_REPORTS so a error-in-a-loop can never storm the log endpoint.
 */
const MAX_REPORTS = 10;

export default function GlobalErrorListener() {
  useEffect(() => {
    const seen = new Set<string>();
    let sent = 0;

    const report = (
      err: Error & { digest?: string },
      context?: string,
    ): void => {
      if (sent >= MAX_REPORTS) return;
      const key = `${err.name}|${err.message}|${context ?? ""}`;
      if (seen.has(key)) return;
      seen.add(key);
      sent++;
      // Never let logging throw inside an error handler.
      void logClientSideError(err, context).catch(() => {});
    };

    const onError = (event: ErrorEvent) => {
      const err =
        event.error instanceof Error
          ? event.error
          : new Error(event.message || "Unknown error");
      // `filename` identifies third-party/injected scripts, which is exactly what
      // the stack alone does not tell us for cross-origin scripts.
      const where = event.filename
        ? `${event.filename}:${event.lineno}:${event.colno}`
        : undefined;
      report(err, where ? `source: ${where}` : undefined);
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const r = event.reason;
      const err =
        r instanceof Error
          ? r
          : new Error(typeof r === "string" ? r : "Unhandled promise rejection");
      report(err, "unhandledrejection");
    };

    // React logs recoverable errors (hydration mismatch #418 among them) through
    // console.error rather than throwing, so this is the only place to observe them.
    const originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      try {
        const first = args[0];
        const text =
          typeof first === "string"
            ? first
            : first instanceof Error
              ? first.message
              : "";
        if (/minified react error #(418|423|425)|hydrat/i.test(text)) {
          const err = first instanceof Error ? first : new Error(text);
          err.name = "HydrationMismatch";
          report(err, "react-recoverable");
        }
      } catch {
        // ignore — never break console.error
      }
      originalConsoleError.apply(console, args as []);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      console.error = originalConsoleError;
    };
  }, []);

  return null;
}
